import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Plus, Search, Upload, UserPlus, Download } from 'lucide-react';
import { getCategoriesForPosition, getOverallScore } from '../data/criteria';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageHeader from '../components/PageHeader';
import AddPlayerDialog from '../components/AddPlayerDialog';
import BulkImportDialog from '../components/BulkImportDialog';
import { PlayersAPI, EvaluationsAPI } from '../api';
import { useToast } from '../hooks/use-toast';

const positionColor = (pos) => {
  if (pos === 'Forward') return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30';
  if (pos === 'Defense') return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
  return 'bg-violet-500/10 text-violet-700 border-violet-500/30';
};

export default function Players() {
  const [q, setQ] = useState('');
  const [pos, setPos] = useState('all');
  const [ageGroup, setAgeGroup] = useState('all');
  const [players, setPlayers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = () => {
    Promise.all([PlayersAPI.list(), EvaluationsAPI.list()])
      .then(([p, e]) => { setPlayers(p); setEvaluations(e); });
  };

  useEffect(() => { refresh(); }, []);

  const { toast } = useToast();

  const exportRoster = () => {
    const rows = list.map(p => ({
      firstName: p.firstName,
      lastName: p.lastName,
      team: p.team,
      jerseyNumber: p.jerseyNumber,
      position: p.position,
      shot: p.shoots === 'Left' ? 'L' : (p.shoots === 'Right' ? 'R' : ''),
      ageGroup: p.ageGroup,
    }));
    if (!rows.length) {
      toast({ title: 'Nothing to export', description: 'No players match the current filters.' });
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['firstName', 'lastName', 'team', 'jerseyNumber', 'position', 'shot', 'ageGroup'],
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Players');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `ShiftNotes_Roster_${stamp}.xlsx`);
    toast({ title: 'Roster exported', description: `${rows.length} players downloaded as xlsx.` });
  };

  const list = useMemo(() => {
    return players.map(p => {
      const evals = evaluations.filter(e => e.playerId === p.id);
      const latest = evals.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      const cats = getCategoriesForPosition(p.position);
      return { ...p, score: latest ? parseFloat(getOverallScore(latest.ratings, cats)) : 0, evalCount: evals.length };
    }).filter(p => {
      const matchQ = !q || `${p.firstName} ${p.lastName} #${p.jerseyNumber}`.toLowerCase().includes(q.toLowerCase());
      const matchPos = pos === 'all' || p.position === pos;
      const matchAge = ageGroup === 'all' || p.ageGroup === ageGroup;
      return matchQ && matchPos && matchAge;
    });
  }, [q, pos, ageGroup, players, evaluations]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Players"
        subtitle={`${list.length} of ${players.length} athletes`}
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportRoster}
              data-testid="export-roster-btn"
              className="border-slate-200 text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4 mr-1.5" /> Export
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)}
              data-testid="bulk-import-btn"
              className="border-slate-200 text-slate-700 hover:bg-slate-50">
              <Upload className="h-4 w-4 mr-1.5" /> Bulk Import
            </Button>
            <Button variant="outline" onClick={() => setAddOpen(true)}
              data-testid="add-player-btn"
              className="border-slate-200 text-slate-700 hover:bg-slate-50">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add Player
            </Button>
            <Button onClick={() => navigate('/app/evaluate/new')}
              data-testid="new-evaluation-btn"
              className="bg-[#1e40af] hover:bg-[#1d4ed8] text-white font-semibold">
              <Plus className="h-4 w-4 mr-1.5" /> New Evaluation
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input placeholder="Search by name or number..." value={q} onChange={e => setQ(e.target.value)}
            className="pl-9 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-sky-500" />
        </div>
        <Select value={pos} onValueChange={setPos}>
          <SelectTrigger className="w-[160px] bg-white border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-900">
            <SelectItem value="all">All Positions</SelectItem>
            <SelectItem value="Forward">Forward</SelectItem>
            <SelectItem value="Defense">Defense</SelectItem>
            <SelectItem value="Goalie">Goalie</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ageGroup} onValueChange={setAgeGroup}>
          <SelectTrigger className="w-[160px] bg-white border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200 text-slate-900">
            <SelectItem value="all">All Age Groups</SelectItem>
            <SelectItem value="U8">U8</SelectItem>
            <SelectItem value="U10">U10</SelectItem>
            <SelectItem value="U12">U12</SelectItem>
            <SelectItem value="U14">U14</SelectItem>
            <SelectItem value="U16">U16</SelectItem>
            <SelectItem value="U19">U19</SelectItem>
            <SelectItem value="U22">U22</SelectItem>
            <SelectItem value="College">College</SelectItem>
            <SelectItem value="Pro">Pro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <AddPlayerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refresh} />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={refresh} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(p => (
          <Link key={p.id} to={`/app/players/${p.id}`}
            className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-sky-500/40 transition-all hover:-translate-y-0.5">
            <div className="flex items-start gap-4">
              <img src={p.avatar} alt="" className="h-14 w-14 rounded-full ring-2 ring-slate-200" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display font-semibold text-slate-900 truncate">{p.firstName} {p.lastName}</div>
                  <span className="text-slate-500 text-sm font-mono">#{p.jerseyNumber}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{p.team}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge className={positionColor(p.position)}>{p.position}</Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-slate-300">{p.shoots}</Badge>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-xs text-slate-500">{p.evalCount} evaluation{p.evalCount !== 1 ? 's' : ''}</div>
              <div className="text-right">
                <div className="font-mono font-bold text-lg text-sky-600">{p.score ? p.score.toFixed(1) : ''}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Overall</div>
              </div>
            </div>
          </Link>
        ))}
        {players.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">Loading players</div>
        )}
        {players.length > 0 && list.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">No players match your filters.</div>
        )}
      </div>
    </div>
  );
}
